<template>
  <div>
    <app-manage-grid
      :groups="tileGroups"
      :loading="fullscreenLoading"
      :title="`${meetingName}（${meetingCode}）`"
      @back="goBack"
    />
    <generate-wx-vue ref="generateWxRef" />
    <export-register ref="exportRegisterRef" />
    <hold-meet-register ref="holdMeetRegisterRef" />
    <hold-meet-user-manage ref="holdMeetUserManageRef" />
  </div>
</template>

<script>
  import generateWxVue from '@/components/generateWx/generateWx.vue'
  import exportRegister from './components/exportRegister.vue'
  import holdMeetRegister from './components/holdMeetRegister.vue'
  import holdMeetUserManage from './components/holdMeetUserManage.vue'
  import appManageGrid from './components/appManageGrid.vue'
  export default {
    name: 'MeetingAppManage',
    components: {
      generateWxVue,
      exportRegister,
      holdMeetRegister,
      holdMeetUserManage,
      appManageGrid,
    },
  }
</script>

<script setup>
  import { computed, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { exportMeetingDaily } from '@/api/exhibitionManage'
  import { downloadFileByUrl } from '@/utils/download'
  import { hasPermission } from '@/utils/permission'

  const route = useRoute()
  const router = useRouter()

  const meetingCode = route.query.meetingCode
  const meetingName = route.query.meetingName
  const isOpenAiAbstractSentence = route.query.isOpenAiAbstractSentence

  // 报名方式配置仅 EVENT_ADMIN 可见（与原 meetingManage state.role 逻辑一致）
  const isEventAdmin = (() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'))
      const roles = Array.isArray(userInfo.role) ? userInfo.role : userInfo.role ? [userInfo.role] : []
      return roles.indexOf('EVENT_ADMIN') > -1
    } catch (e) {
      return false
    }
  })()

  const generateWxRef = ref(null)
  const exportRegisterRef = ref(null)
  const holdMeetRegisterRef = ref(null)
  const holdMeetUserManageRef = ref(null)
  const fullscreenLoading = ref(false)

  const goBack = () => router.go(-1)

  const push = (path, query) => router.push({ path, query })

  const rowObj = () => ({
    code: meetingCode,
    meetingCode,
    meetingName,
    isOpenAiAbstractSentence,
    relationType: 'HOLD_MEETING',
  })

  // 预览
  const openPreview = () => {
    const row = rowObj()
    const qrCodeParams = {
      autoColor: false,
      b: 0,
      env: '',
      g: 0,
      jumpPath: row.meetingCode,
      jumpType: '',
      logo: '',
      params: `{"meetingCode":"${row.meetingCode}"}`,
      path: 'pages/index/index',
      r: 0,
      scenesType: 'USER_HOLD_MEETING',
      width: 1280,
      miniPath: `subPackage/meet/holdMeet/detail?code=${row.meetingCode}`,
    }
    generateWxRef.value.handleOpen(row, qrCodeParams)
  }

  // 报名信息导出（打开 exportRegister 抽屉）
  const openRegisterExport = () => {
    const row = rowObj()
    exportRegisterRef.value.handleOpen(row, `报名信息导出`, `MEETING_REGISTER_REPORT`)
  }

  // 会议日程导出（直接下载文件）
  const meetingDailyExportApi = () => {
    exportMeetingDaily(meetingCode).then(({ data: { fileUrl, fileName } }) => downloadFileByUrl(fileUrl, fileName))
  }

  const visible = (item) => (!item.perm || hasPermission(item.perm)) && (!item.gate || item.gate())

  const allGroups = [
    {
      name: '管理',
      items: [
        { label: '预览', icon: 'View', perm: ['user:qrcode:getQrCodeUrls'], action: openPreview },
        // 内容编辑入口暂隐藏（后端 admin 端点联调前先下线，避免 401 登出；路由与页面保留，后端就绪后取消注释即可）
        // {
        //   label: '内容编辑',
        //   icon: 'Notebook',
        //   action: () => push('/exhibitionManage/meetingContent', { meetingCode, meetingName }),
        // },
        {
          label: '注册管理',
          icon: 'List',
          perm: ['user:admin:attend:auditList'],
          action: () => push('/exhibitionManage/registerManage', { meetingCode, meetingType: 'HOLD_MEETING' }),
        },
        {
          label: '报名方式配置',
          icon: 'Setting',
          gate: () => isEventAdmin,
          action: () => holdMeetRegisterRef.value.handleOpen(rowObj()),
        },
        {
          label: '协同管理',
          icon: 'UserFilled',
          perm: ['TransferTeamwork'],
          action: () => holdMeetUserManageRef.value.handleOpen(rowObj()),
        },
      ],
    },
    {
      name: '渠道',
      items: [
        {
          label: '分享渠道',
          icon: 'Share',
          perm: ['user:register:channel:list'],
          action: () => push('/exhibitionManage/shareChannel', { meetingCode }),
        },
        {
          label: '邀请码渠道',
          icon: 'Message',
          perm: ['user:register:channel:list'],
          action: () => push('/exhibitionManage/shareCodeChannel', { meetingCode, meetingType: 'HOLD_MEETING' }),
        },
      ],
    },
    {
      name: '数据',
      items: [
        {
          label: '报名结果订阅',
          icon: 'Message',
          perm: ['user:admin:attend:audit'],
          action: () => push('/exhibitionManage/meetingSubscription', { meetingCode, meetingName }),
        },
        { label: '报名信息导出', icon: 'Document', action: openRegisterExport },
        { label: '会议日程导出', icon: 'Download', kind: 'export', action: meetingDailyExportApi },
        {
          label: '用户日报',
          icon: 'Memo',
          perm: ['user:admin:event:report:meeting:dailyDetail'],
          action: () => push('/daliyReport', { meetingCode, meetingType: 'HOLD_MEETING', meetingName }),
        },
      ],
    },
    {
      name: 'AI',
      items: [
        {
          label: 'AI视频工作台',
          icon: 'VideoCamera',
          action: () => push('/exhibitionManage/aiVideoScript', { meetingCode, meetingName }),
        },
        {
          label: '原声逐字播放',
          icon: 'Headset',
          action: () => push('/exhibitionManage/meetingTranscript', { meetingCode, meetingName }),
        },
        {
          label: 'AI金句',
          icon: 'ChatLineSquare',
          action: () =>
            push('/exhibitionManage/aiSentence', {
              meetingCode,
              type: 'HOLD_MEETING',
              isOpenAiAbstractSentence,
              meetingName,
            }),
        },
      ],
    },
  ]

  const tileGroups = computed(() =>
    allGroups.map((g) => ({ name: g.name, items: (g.items || []).filter(visible) })).filter((g) => g.items.length)
  )
</script>
